import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Feather, Menu, X } from "lucide-react";
import AdminMenu, { ADMIN_LINKS, useIsAdmin } from "@/components/AdminMenu";

const navLinks = [
  { to: "/", label: "Home", end: true },
  { to: "/books/suspicious-times", label: "Books" },
  { to: "/books/pixels-and-glass", label: "The Song" },
  { to: "/about", label: "The Author" },
  { to: "/reviews", label: "Reviews" },
  { to: "/reader", label: "E-Reader" },
  { to: "/account", label: "Account" },
];

export default function Layout() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isAdmin = useIsAdmin();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setOpen(false);
  }, [pathname]);

  return (
    <div className="relative z-10 min-h-screen flex flex-col">
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? "backdrop-blur-xl bg-[#0B0C10]/70 border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <Feather className="w-5 h-5 text-primary-gold transition-transform group-hover:-rotate-12" />
            <span className="font-serif-display text-2xl tracking-tight leading-none">
              The Leckie <span className="text-primary-gold">Novels</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `font-mono-ui text-xs uppercase tracking-[0.18em] transition-colors hover:text-primary-gold ${
                    isActive ? "text-primary-gold" : "text-soft"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {isAdmin && <AdminMenu />}
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-text-primary"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-white/10 bg-[#0B0C10]/95 backdrop-blur-xl px-6 py-6 flex flex-col gap-5">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className="font-mono-ui text-sm uppercase tracking-[0.18em] text-soft hover:text-primary-gold"
              >
                {l.label}
              </NavLink>
            ))}
            {isAdmin && (
              <div className="border-t border-white/10 pt-5 flex flex-col gap-4" data-testid="mobile-admin-menu">
                <p className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-primary-gold">Admin</p>
                {ADMIN_LINKS.map(({ to, label, icon: Icon, testId }) => (
                  <Link key={to} to={to} data-testid={`mobile-${testId}`} className="flex items-center gap-3 font-mono-ui text-sm uppercase tracking-[0.18em] text-soft hover:text-primary-gold">
                    <Icon className="w-4 h-4" /> {label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer data-testid="site-footer" className="border-t border-white/10 mt-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 grid md:grid-cols-2 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Feather className="w-5 h-5 text-primary-gold" />
              <span className="font-serif-display text-2xl">The Leckie Novels</span>
            </div>
            <p className="text-soft text-sm max-w-xs leading-relaxed">
              The official storefront of Australian author Michael Leckie. Thrillers, time travel,
              and tales of survival.
            </p>
          </div>
          <div>
            <p className="font-mono-ui text-xs uppercase tracking-[0.2em] text-primary-gold mb-5">
              Explore
            </p>
            <ul className="space-y-3 text-sm">
              {navLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-soft hover:text-text-primary transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
          <p className="font-mono-ui text-xs text-soft">
            © {new Date().getFullYear()} Michael Leckie · All rights reserved
          </p>
          <Link
            to="/admin"
            data-testid="footer-admin-link"
            className="font-mono-ui text-xs uppercase tracking-[0.18em] text-soft/60 hover:text-primary-gold transition-colors"
          >
            Admin Login
          </Link>
        </div>
      </footer>
    </div>
  );
}
